// src/pages/BcpWeatherInfo.jsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from "../lib/api";
import { GPS_UNSUPPORTED } from './BCPEmployee';
import './BcpWeatherInfo.css'; 

const WeatherInfo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  

  const { lat, lng, gpsErrorCode } = location.state || { lat: null, lng: null };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weatherData, setWeatherData] = useState({
    home: null,       
    current: null,     
    prefectures_list: [],
    isAtHome: false
  });

  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        setLoading(true);
     
        const response = await api.get(`/weather-bcp/?lat=${lat}&lng=${lng}`);
        const data = response.data;

        setWeatherData({
          home: data.home_weather,
          current: data.current_weather,
          prefectures_list: data.prefectures_list,
          isAtHome: data.is_at_home
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWeatherData();
  }, [lat, lng]);


  const getLevelStyle = (level) => {
    const lvl = parseInt(level, 10) || 1; 
    switch (lvl) {
      case 5: return { class: 'level-5-black', text: 'レベル5: 特別警報 (緊急安全確保)' };
      case 4: return { class: 'level-4-purple', text: 'レベル4: 危険警報 (避難指示)' };
      case 3: return { class: 'level-3-red', text: 'レベル3: 警報 (高齢者等避難)' };
      case 2: return { class: 'level-2-yellow', text: 'レベル2: 注意報 (大雨/洪水注意報)' };
      default: return { class: 'level-1-white', text: 'レベル1: 早期注意情報 (通常)' };
    }
  };

  // GPS が取れなかった理由ごとの案内。code は GeolocationPositionError の
  // 1=権限拒否 / 2=取得不能 / 3=タイムアウト、0 は BCPEmployee.js が付ける非対応コード。
  // 理由を出さないと、ブラウザの権限が「許可しない」になっている社員が原因に辿り着けない。
  const getGpsErrorMessage = (code) => {
    switch (code) {
      case 1:
        return {
          title: '🚫 位置情報の利用がブロックされています。',
          hint: 'アドレスバー左のアイコン（🔒）→「サイトの設定」→「位置情報」を「許可」に変更し、'
            + '画面を再読み込みしてからもう一度お試しください。'
            + '端末の設定（設定 → 位置情報）が OFF になっていないかもご確認ください。',
        };
      case 2:
        return {
          title: '📴 現在地を特定できませんでした。',
          hint: '端末の位置情報（GPS）が OFF になっているか、電波の届きにくい場所にいる可能性があります。'
            + '端末の設定で位置情報を ON にしてからお試しください。',
        };
      case 3:
        return {
          title: '⌛ 位置情報の取得がタイムアウトしました。',
          hint: '許可を確認するダイアログが表示された場合は「許可」をタップしてください。'
            + '屋内では時間がかかることがあります。もう一度お試しください。',
        };
      case GPS_UNSUPPORTED:
        return {
          title: '📴 このブラウザは位置情報に対応していません。',
          hint: '下のエリア選択から都道府県を手動で切り替えてください。',
        };
      default:
        // 直接 URL を開いた場合など、失敗理由が分からないとき（従来の文言）。
        return {
          title: '📴 位置情報（GPS）から気象情報を取得できませんでした。',
          hint: '出張先や外出先の警告を確認したい場合は、端末のGPSをONにするか、下のエリア選択から手動で切り替えてください。',
        };
    }
  };

  const gpsError = getGpsErrorMessage(gpsErrorCode);

  // 端末では位置情報が取れたが、サーバ側で地域を特定できなかった場合。
  // 端末側の案内（GPSをONに等）を出しても解決しないため、理由を分けて表示する。
  const getCurrentAreaMessage = () => {
    switch (weatherData.current?.status) {
      case 'geocode_failed':
        return {
          title: '📍 現在地の地域を特定できませんでした。',
          hint: '位置情報は取得できましたが、地域の判定に失敗しました。'
            + '時間をおいて再度お試しいただくか、下のエリア選択から手動で切り替えてください。',
        };
      case 'area_not_supported':
        return {
          title: '🌏 気象庁の対象地域外です。',
          hint: '現在地が気象庁の警報・注意報の対象地域外のため表示できません。'
            + '下のエリア選択から都道府県を切り替えてください。',
        };
      default:
        return gpsError;
    }
  };

  const currentMessage = getCurrentAreaMessage();

  // 手動でエリアを選んだ状態か（BCPEmployee.js からの GPS 遷移では lat は数値）。
  const isManual = lat === 'manual';

  // 都道府県の手動選択。BLOCK 3 として画面下部に常設する（GPS の成否・自宅にいるかを問わない）。
  // replace にしないと選び直すたびに履歴が積まれ、「戻る」でBCPメニューに戻れなくなる。
  const prefSelector = (
    <select
      className="pref-selector"
      value={isManual ? lng : ''}
      onChange={(e) => {
        if (!e.target.value) return;
        navigate('.', { replace: true, state: { lat: 'manual', lng: e.target.value } });
      }}
    >
      <option value="">-- 都道府県を手動で選択 --</option>
      {weatherData.prefectures_list?.map((pref) => (
        <option key={pref.code} value={pref.code}>
          {pref.name}
        </option>
      ))}
    </select>
  );

  // 登録住所から地域を判定できなかった場合（住所未登録など）。従来は「大阪府」を既定表示に
  // していたため、府外の社員にも大阪府の情報が自宅として出ていた。
  const homeUnknown = weatherData.home?.status === 'home_unknown';

  const homeLevel = parseInt(weatherData.home?.max_level, 10) || 1;
  const currentLevel = parseInt(weatherData.current?.max_level, 10) || 1;

  // レベル3（警報／高齢者等避難）以上を「警戒中」とみなし、カードを強調して避難所への導線を出す。
  const ALERT_LEVEL = 3;
  const homeAlert = homeLevel >= ALERT_LEVEL;
  const currentAlert = currentLevel >= ALERT_LEVEL;

  if (loading) {
    return (
      <div className="weather-info-page loading-box">
        <div className="spinner"></div>
        <p>気象庁から最新の防災情報を取得中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-info-page error-box">
        <p>エラーが発生しました: {error}</p>
        <button className="bcp-btn-back" onClick={() => navigate(-1)}>戻る</button>
      </div>
    );
  }

  return (
    <div className="weather-info-page">
      <header className="weather-header">
        <h3>防災気象情報 (BCP管理)</h3>
        <p className="refresh-note">
          ※ 気象庁<br />（JMA）のリアルタイムデータと連携しています。 
        </p>
      </header>

      <div className="weather-container">
        
        {/* BLOCK 1: 登録住所（自宅周辺） */}
        <section className={`weather-card home-card${homeAlert && !homeUnknown ? ' card-alert' : ''}`}>
          <div className="card-header">
            <h3>🏠 登録住所（自宅周辺）</h3>
            <span className="area-tag">
              {homeUnknown
                ? '住所未判定'
                : `${weatherData.home?.prefecture || ''}${weatherData.home?.city || ''}`}
            </span>
          </div>

          {homeUnknown ? (
            <div className="card-body gps-disabled">
              <p>🏠 登録住所から地域を判定できませんでした。</p>
              <p className="sub-text">
                住所が未登録か、都道府県・市区町村を読み取れない形式で登録されています。
                管理者に住所の登録内容をご確認ください。現在地の情報は下に表示されます。
              </p>
            </div>
          ) : (
            <>
              <div className={`level-badge ${getLevelStyle(weatherData.home?.max_level).class}`}>
                {getLevelStyle(weatherData.home?.max_level).text}
              </div>
              <div className="card-body">
                <h4>発令中の警報・注意報:</h4>
                <ul className="warning-list">
                  {weatherData.home?.warnings && weatherData.home.warnings.length > 0 ? (
                    weatherData.home.warnings.map((warn, index) => <li key={index}>⚠️ {warn}</li>)
                  ) : (
                    <li className="no-warning">🟢 現在、警報・注意報はありません。</li>
                  )}
                </ul>

                {weatherData.isAtHome && homeAlert && (
                  <div className="shelter-link">
                    <Link to="/bcp/home-shelter" className="btn-shelter"> 
                     お近くの避難所情報
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* BLOCK 2: 現在地の情報。
            自宅にいるときは自宅ブロックと重複するため出さないが、手動でエリアを選んだ場合は
            選んだ地域の情報をここに表示するため、自宅と同じ都道府県でも出す。*/}
        {(!weatherData.isAtHome || isManual || homeUnknown) && (
          <section className={`weather-card current-card${currentAlert ? ' card-alert' : ''}`}>
            <div className="card-header">
              <h3>📍 現在地の情報</h3>
              <span className="area-tag">
           
                {weatherData.current?.prefecture ? `${weatherData.current.prefecture}${weatherData.current.city || ''}` : (lat && lng ? '位置特定中' : 'GPS未設定')}
              </span>
            </div>

           
            {weatherData.current?.prefecture && weatherData.current?.max_level ? (
              <>
                <div className={`level-badge ${getLevelStyle(weatherData.current?.max_level).class}`}>
                  {getLevelStyle(weatherData.current?.max_level).text}
                </div>
                <div className="card-body">
                  <h4>発令中の警報・注意報:</h4>
                  <ul className="warning-list">
                    {weatherData.current?.warnings && weatherData.current.warnings.length > 0 ? (
                      weatherData.current.warnings.map((warn, index) => <li key={index}>⚠️ {warn}</li>)
                    ) : (
                      <li className="no-warning">🟢 現在、警報・注意報はありません。</li>
                    )}
                  </ul>
                  {currentAlert && (
                    <div className="shelter-link">
                      <Link to="/bcp/nearby-shelter" className="btn-shelter"> 
                      お近くの避難所情報
                      </Link>
                    </div>
                  )}
                </div>
              </>
            ) : (
              
              <div className="card-body gps-disabled">
                <p>{currentMessage.title}</p>
                <p className="sub-text">{currentMessage.hint}</p>
              </div>
            )}
          </section>
        )}

        {/* BLOCK 3: 他の地域の情報。GPS の成否や自宅にいるかに関わらず常設する。
            以前はセレクタが現在地ブロックの中（GPS 失敗時のフォールバック UI）にしか無かったため、
            GPS が取得できるようになった結果、出張先などの警報を見る導線が消えていた。*/}
        <section className="area-switcher">
          <h4>他の地域の情報を見る</h4>
          {prefSelector}
          <p className="sub-text">出張先・外出先など、任意の都道府県の警報・注意報を確認できます。</p>
        </section>
      </div>

      <div className="action-footer">
        <button type="button" className="bcp-btn-back" onClick={() => navigate(-1)}>
          戻る
        </button>
      </div>
    </div>
  );
};

export default WeatherInfo;